import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ThemeService } from '../service/theme.service';
import { ClientService } from '../service/client.service';
import { firstValueFrom } from 'rxjs';
import { DashboardService } from '../service/dashboard.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { CognitoService } from '../service/cognito.service';
import { FeeInputService } from '../service/fee-input.service';

type CostTemplateForm = {
  client_id: string;
  device_count: number | null;
  service_fee: string;
  subscription_fee: string;
  device_fee: string;
  additional_fee: string;
};

type ClientSummary = {
  client_id: string;
  client_name?: string;
  legal_name?: string;
};

@Component({
  selector: 'app-cost-edit',
  templateUrl: './cost-edit.component.html',
  standalone: false,
  styleUrls: ['./cost-edit.component.css', '../../global-elements.css'],
})
export class CostEditComponent implements OnInit {
  // Bound form model for template-driven inputs.
  form: CostTemplateForm = {
    client_id: '',
    device_count: null,
    service_fee: '',
    subscription_fee: '',
    device_fee: '',
    additional_fee: '',
  };
  // UI state flags.
  isSubmitting = false;
  isDeleting = false;
  isLoadingTemplate = false;
  showDeleteConfirm = false;
  // Display-only legal name shown above the form.
  clientLegalName = '';
  // Preserve created timestamp when updating existing templates.
  private templateCreatedTimestamp: number | null = null;

  constructor(
    private route: ActivatedRoute,
    public theme: ThemeService,
    private clientService: ClientService,
    private dashboardService: DashboardService,
    private http: HttpClient,
    private translate: TranslateService,
    private router: Router,
    private cognitoService: CognitoService,
    public feeInput: FeeInputService,
  ) {
    // Restrict access to administrators.
    this.cognitoService.getCurrentRole(
      [environment.users.role.administrator],
      [environment.users.superAdmin],
    );
  }

  ngOnInit(): void {
    // Client ID is passed via /cost/:id.
    const clientId = this.route.snapshot.paramMap.get('id');
    this.form.client_id = clientId ?? '';
    if (this.form.client_id) {
      this.loadClientLegalName(this.form.client_id);
      this.loadDeviceCount(this.form.client_id);
      this.loadInvoiceTemplate(this.form.client_id);
    }
  }

  // Fetches the client legal name to display in the header.
  async loadClientLegalName(clientId: string): Promise<void> {
    try {
      const response = await firstValueFrom(this.clientService.getClients());
      const clients: ClientSummary[] = Array.isArray(response) ? response : [];
      const client = clients.find((c) => c.client_id === clientId);
      this.clientLegalName =
        client?.legal_name || client?.client_name || clientId;
    } catch (error) {
      console.error('[CostEdit] Failed to load client name', error);
      this.clientLegalName = clientId;
    }
  }

  // Device count is derived from the client/entity relationships (thing names).
  async loadDeviceCount(clientId: string): Promise<void> {
    try {
      const thingNames =
        await this.dashboardService.getThingNamesByEntityId(clientId);
      this.form.device_count = Array.isArray(thingNames)
        ? thingNames.length
        : 0;
    } catch (error) {
      console.error('[CostEdit] Failed to load device count', error);
      this.form.device_count = 0;
    }
  }

  // Loads existing invoice template data to populate the fee fields.
  async loadInvoiceTemplate(clientId: string): Promise<void> {
    this.isLoadingTemplate = true;
    try {
      const url =
        environment.api.stage +
        environment.api.route.getClientInvoiceTemplate +
        '&client_id=' +
        clientId;
      const response = await firstValueFrom(this.http.get<any>(url));
      const template = Array.isArray(response) ? response[0] : response;
      if (template) {
        this.form.service_fee = this.formatFeeValue(template.service_fee);
        this.form.subscription_fee = this.formatFeeValue(
          template.subscription_fee,
        );
        this.form.device_fee = this.formatFeeValue(template.device_fee);
        this.form.additional_fee = this.formatFeeValue(template.additional_fee);
        this.templateCreatedTimestamp =
          template.created != null ? Number(template.created) : null;
      }
    } catch (error) {
      console.error('[CostEdit] Failed to load invoice template', error);
    } finally {
      this.isLoadingTemplate = false;
    }
  }

  // Submits the template (create or update) via the create route.
  onSubmit(isValid: boolean): void {
    if (!isValid) {
      return;
    }

    this.isSubmitting = true;
    const payload = {
      ...this.form,
      service_fee: this.parseFeeValue(this.form.service_fee),
      subscription_fee: this.parseFeeValue(this.form.subscription_fee),
      device_fee: this.parseFeeValue(this.form.device_fee),
      additional_fee: this.parseFeeValue(this.form.additional_fee),
      created: this.templateCreatedTimestamp ?? Math.floor(Date.now() / 1000),
      modified: Math.floor(Date.now() / 1000),
    };

    const url =
      environment.api.stage + environment.api.route.createClientInvoiceTemplate;

    this.http.post(url, payload).subscribe({
      next: () => {
        this.isSubmitting = false;
      },
      error: (error) => {
        console.error('[CostEdit] Failed to save template', error);
        this.isSubmitting = false;
      },
    });
  }

  // Opens the custom delete confirmation dialog.
  openDeleteConfirm(): void {
    if (!this.form.client_id || this.isDeleting) {
      return;
    }
    this.showDeleteConfirm = true;
  }

  // Closes the delete confirmation dialog.
  cancelDelete(): void {
    this.showDeleteConfirm = false;
  }

  // Deletes the existing template for the current client.
  confirmDelete(): void {
    if (!this.form.client_id || this.isDeleting) {
      return;
    }
    this.isDeleting = true;
    const url =
      environment.api.stage + environment.api.route.deleteClientInvoiceTemplate;
    const payload = { client_id: this.form.client_id };

    this.http.post(url, payload).subscribe({
      next: () => {
        this.form.service_fee = '';
        this.form.subscription_fee = '';
        this.form.device_fee = '';
        this.form.additional_fee = '';
        this.templateCreatedTimestamp = null;
        this.isDeleting = false;
        this.showDeleteConfirm = false;
      },
      error: (error) => {
        console.error('[CostEdit] Failed to delete template', error);
        this.isDeleting = false;
        this.showDeleteConfirm = false;
      },
    });
  }

  // Ensures a consistent 2-decimal display for fee inputs.
  formatFeeValue(value: number | string | null | undefined): string {
    if (value === null || value === undefined || value === '') return '';
    const numericValue = typeof value === 'string' ? value : String(value);
    return this.feeInput.formatFeeValue(numericValue);
  }

  // Formats fee on blur without wiping non-empty values.
  onFeeBlur(
    field: keyof Pick<
      CostTemplateForm,
      'service_fee' | 'subscription_fee' | 'device_fee' | 'additional_fee'
    >,
  ): void {
    if (this.form[field]) {
      this.form[field] = this.formatFeeValue(this.form[field]);
    }
  }

  // Sanitizes invalid characters from input text (digits and a single dot only).
  onFeeInput(event: Event): void {
    this.feeInput.onInput(event);
  }

  // Blocks invalid keypresses to avoid non-numeric input.
  onFeeKeydown(event: KeyboardEvent): void {
    this.feeInput.onKeydown(event);
  }

  // Sanitizes pasted text to prevent invalid characters or extra dots.
  onFeePaste(event: ClipboardEvent): void {
    this.feeInput.onPaste(event);
  }

  // Converts the display string to a numeric payload value.
  private parseFeeValue(value: string): number | null {
    if (!value) {
      return null;
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  }

  // Return to the client list.
  cancel(): void {
    this.router.navigate(['/entities']);
  }
}
